<template>
  <div class="flex flex-col gap-1">
    <div class="font-bold p-2 flex">
      Section&nbsp;
      <button
        v-if="isFinite(Number(unit.uuid))"
        v-tippy="'Click to copy section id'"
        @click="navigator.clipboard.writeText(unit.uuid)"
      >
        <SMIcon icon="Clipboard" size="1em" />
      </button>
      <span v-if="unit.category?.bestAvailable" class="ml-2 flex items-center uppercase underline text-xs">
        <SMIcon icon="best" class="pr-1" size="1.2em" />
        Best Available
      </span>
    </div>
    <div class="flex px-2">
      <SMButton
        v-tippy="'Edit Section to change its content'"
        :disabled="isGa || splitModeUnit || resizedUnit"
        label="Edit Contents"
        type="primary"
        @click="edit"
      />
    </div>
    <div class="flex gap-1 px-2 pb-2 text-gray-300 flex-wrap">
      <SMButton
        v-tippy="splitModeUnit === unit.uuid ? 'End Split Section' : 'Split Section'"
        :disabled="isGa || eventIsPublished || resizedUnit || !hasUnits"
        icon="split"
        :type="splitModeUnit ? 'warning' : undefined"
        @click="split"
      />
      <SMButton
        v-tippy="resizedUnit ? 'End Resizing Section' : 'Resize Section'"
        icon="scale"
        :type="resizedUnit ? 'warning' : undefined"
        :disabled="splitModeUnit"
        @click="resize"
      />
      <SMButton v-tippy="'Flip Horizontally'" icon="flip-h" @click="flipHorizontally" />
      <SMButton v-tippy="'Flip Vertically'" icon="flip-v" @click="flipVertically" />
      <SMButton v-tippy="'Duplicate Section'" icon="copy" :disabled="eventIsPublished" @click="duplicate" />
      <SMButton v-tippy="'Delete Section'" icon="delete" @click="remove" />
    </div>

    <ul class="space-y-2 px-2">
      <li class="flex gap-2 items-center">
        <SMInput
          v-model="label"
          :disabled="!!protectedFields?.includes('name')"
          placeholder="Section Name"
          @change="onLabelChange"
        />
        <SMCheckbox v-model="visible" v-tippy="'Visible'" :disabled="eventIsPublished">Visible</SMCheckbox>
      </li>

      <li v-if="!hasServerSections" class="flex gap-2 items-center h-15">
        <SMSelect
          :model-value="categoryId"
          :disabled="eventIsPublished"
          placeholder="Category..."
          @update:modelValue="onCategorySelection"
        >
          <SMOption
            v-for="option in categories"
            :key="option.id"
            :value="option.id"
            :disabled="option.id === 0"
            class="flex gap-2 items-center"
          >
            <div v-if="option.id" class="w-5 h-5 shrink-0" :style="{ background: option.color }" />
            <div class="text-ellipsis grow shrink overflow-hidden">{{ option.name }} ({{ option.price }})</div>
          </SMOption>
        </SMSelect>
        <SMCheckbox v-model="isGa" v-tippy="'General Admission'" :disabled="hasUnits || eventIsPublished">
          GA
        </SMCheckbox>
      </li>

      <li v-else class="flex gap-2 items-center h-15">
        <SMSelect
          :model-value="serverSectionId"
          :disabled="eventIsPublished"
          placeholder="Category..."
          clearable
          @update:modelValue="onServerSectionIdSelection"
        >
          <template v-for="(option, index) in serverSectionOptions" :key="option.id">
            <div
              v-if="!serverSectionOptions[index - 1] || serverSectionOptions[index - 1].type !== option.type"
              class="p-1 bg-gray-200 text-xs"
            >
              {{ typeLabelMap[option.type] }}
            </div>
            <SMOption :value="option.id" class="flex gap-2 items-center">
              <div class="w-5 h-5 shrink-0" :style="{ background: option.color }" />
              <div class="text-ellipsis grow shrink overflow-hidden">{{ option.name }} / {{ option.categoryName }}</div>
              <div class="shrink-0">&nbsp;({{ option.price }})</div>
            </SMOption>
          </template>
        </SMSelect>
      </li>
    </ul>

    <div class="md-subheading px-2 my-2 bg-gray-200">Attributes</div>
    <ul class="space-y-2 p-2">
      <li>
        <SMInput
          v-model="unit.attributes.sectionCode"
          v-tippy="'Section Code'"
          icon="code"
          placeholder="Section Code"
          :disabled="!!protectedFields?.includes('code')"
          @update:model-value="$app.seatmap.triggerUnsavedChanges(true)"
        />
      </li>
      <li>
        <SMInput
          v-model="unit.attributes.level"
          v-tippy="'Level'"
          icon="level"
          placeholder="Level"
          :disabled="!!protectedFields?.includes('level')"
          @update:model-value="$app.seatmap.triggerUnsavedChanges(true)"
        />
      </li>
      <li>
        <SMInput
          v-model="unit.attributes.entrance"
          v-tippy="'Entrance'"
          icon="entrance"
          placeholder="Entrance"
          :disabled="!!protectedFields?.includes('entrance')"
          @update:model-value="$app.seatmap.triggerUnsavedChanges(true)"
        />
      </li>
    </ul>

    <div class="md-subheading px-2 my-2 bg-gray-200">Styles</div>
    <ul class="space-y-2 p-2">
      <li>
        <SMNumberInput
          v-model="opacity"
          v-tippy="'Opacity'"
          :min="0"
          :max="100"
          icon="opacity"
          @change="onOpacityChanged"
        />
      </li>
      <li>
        <div class="flex gap-2 items-center">
          <SMColorPicker
            v-model="fontColor"
            v-tippy="'Label Color'"
            icon="text-color"
            class="grow"
            @update:modelValue="onLabelColorChanged"
          />
        </div>
      </li>
      <li>
        <SMNumberInput
          v-model="fontSizeValue"
          v-tippy="'Font Size'"
          icon="font-size"
          :min="1"
          :max="200"
          @update:modelValue="onFontSizeValueChange"
        />
      </li>
      <li>
        <SMNumberInput
          v-model="hOffsetValue"
          v-tippy="'Label Horizontal Position'"
          icon="hOffset"
          :min="-300"
          :max="300"
          @update:modelValue="onHOffsetValueChange"
        />
      </li>
      <li>
        <SMNumberInput
          v-model="vOffsetValue"
          v-tippy="'Label Vertical Position'"
          icon="vOffset"
          :min="-300"
          :max="300"
          @update:modelValue="onVOffsetValueChange"
        />
      </li>
      <li>
        <SMNumberInput
          v-model="rotationValue"
          v-tippy="'Label Rotation'"
          icon="text-rotate"
          :min="-180"
          :max="180"
          @update:modelValue="onRotationValueChange"
        />
      </li>
      <li v-if="isPolygon">
        <SMNumberInput
          v-model="smoothingValue"
          v-tippy="'Smoothing'"
          icon="corner-radius"
          :min="0"
          :max="100"
          :step="1"
          @update:modelValue="onSmoothingChange"
        />
      </li>
      <li v-if="isRect">
        <SMNumberInput
          v-model="cornerRadius.value"
          v-tippy="'Smoothing'"
          icon="corner-radius"
          :min="0"
          :max="100"
          @update:modelValue="onCornerRadiusChange"
        />
      </li>
    </ul>
    <SMDialogConfirm
      ref="confirmServerSectionIdAssignmentRef"
      md-title="Confirm Assignment"
      :md-content-html="`
        You are trying to assign a &quot;General Admission&quot; category to a section that contains seats.
        Are you sure you want to continue?
      `"
      md-ok-text="OK"
      md-cancel-text="Cancel"
      @close="serverSectionIdAssignmentDialogClosed"
    />
    <SMDialogConfirm
      ref="confirmCategoryAssignmentRef"
      md-title="Confirm Assignment"
      :md-content-html="`
        You are trying to assign a &quot;General Admission&quot; category to a section that contains seats.
        Are you sure you want to continue?
      `"
      md-ok-text="OK"
      md-cancel-text="Cancel"
      @close="categoryAssignmentDialogClosed"
    />
  </div>
</template>
<script lang="ts">
import first from 'lodash/first';
import eventbus from 'tiny-emitter/instance';
import { mapActions, mapState } from 'vuex';
import { TEXT, FLIP, TYPE, EVENTSTATUS } from '@/constants';
import tinycolor from 'tinycolor2';
import SMInput from '@/components/ui/SMInput.vue';
import SMButton from '@/components/ui/SMButton.vue';
import SMIcon from '@/components/ui/SMIcon.vue';
import SMNumberInput from '@/components/ui/SMNumberInput.vue';
import SMCategorySelect from '@/components/ui/SMCategorySelect.vue';
import SMColorPicker from '@/components/ui/SMColorPicker.vue';
import { lastUsedSectionFontSize } from '@/core/utils';
import SMSelect from '@/components/ui/SMSelect.vue';
import SMOption from '@/components/ui/SMOption.vue';
import SMCheckbox from '@/components/ui/legacy/SMCheckbox.vue';
import keyBy from 'lodash/keyBy';
import SMDialogConfirm from '@/components/ui/legacy/SMDialogConfirm.vue';
import { PropType } from 'vue';
import { Section } from '@/core/models/sections/section';
import RectangularSection from '@/core/models/sections/rect';
import PathSection from '@/core/models/sections/path';

const blackRgba = tinycolor('black').toRgb();

export default {
  components: {
    SMInput,
    SMButton,
    SMIcon,
    SMNumberInput,
    SMColorPicker,
    SMSelect,
    SMOption,
    SMCheckbox,
    SMDialogConfirm,
  },
  props: {
    units: {
      type: Array as PropType<Section[]>,
      required: true,
    },
  },
  data() {
    return {
      color: {
        rgba: blackRgba,
      },
      fontColor: 'black',
      rotationValue: 0,
      rotation: {
        tooltip: 'hover',
        width: 200,
        min: -180,
        max: 180,
        interval: 1,
      },
      smoothingValue: 0,
      smoothing: {
        tooltip: 'hover',
        width: 200,
        min: 0,
        max: 0.2,
        interval: 0.01,
      },
      hOffsetValue: 0,
      hOffset: {
        tooltip: 'hover',
        width: 200,
        min: -300,
        max: 300,
        interval: 1,
        formatter: Math.trunc,
      },
      vOffsetValue: 0,
      vOffset: {
        tooltip: 'hover',
        width: 200,
        min: -300,
        max: 300,
        interval: 1,
        formatter: Math.trunc,
      },
      fontSizeValue: lastUsedSectionFontSize.value,
      fontSize: {
        tooltip: 'hover',
        width: 200,
        min: 20,
        max: 200,
        interval: 1,
      },
      label: '',
      cornerRadius: {
        tooltip: false,
        width: 200,
        value: 0,
        min: 0,
        max: 100,
        interval: 5,
      },
      isGa: false,
      opacity: this.units[0].attributes.opacity ?? 100,
      serverSectionId: null as string | null,
      categoryId: null as number | null | undefined,
      protectedFields: [] as Array<string>,
      entrance: '',
      typeLabelMap: {
        seated: 'Seated',
        'seated-best': 'Seated Best Available',
        ga: 'General Admission',
      },
      navigator,
    };
  },
  computed: {
    ...mapState('seatmap', ['isResizing', 'resizedUnit', 'resizedUnitSmoothing', 'splitModeUnit']),
    hasServerSections() {
      return !!this.$app.props.sections;
    },
    serverSection() {
      return this.$app.props.sections?.items.find((section) => section.id === this.serverSectionId);
    },
    serverSectionOptions() {
      const categories = this.$app.props.categoriesList;

      const usedSections = this.$app.seatmap.sections
        .map((section) => section.attributes.serverSectionId)
        .filter(Boolean);

      const filteredSections = this.$app.props.sections?.items.filter(
        (section) => !usedSections.includes(section.id) || section.id === this.serverSectionId
      );

      const catById = keyBy(categories, 'id');
      const sortedTypes = ['seated', 'seated-best', 'ga'];

      return (
        filteredSections
          ?.map((section) => {
            const category = catById[section.categoryId];
            if (!category) return;
            return {
              id: section.id,
              name: section.name,
              categoryName: category.name,
              price: category.price,
              color: category.color,
              type: (category.type + (category.bestAvailable ? '-best' : '')) as 'seated' | 'ga' | 'seated-best',
            };
          })
          .filter(Boolean) ?? []
      ).sort((a, b) => {
        const aIndex = sortedTypes.indexOf(a.type);
        const bIndex = sortedTypes.indexOf(b.type);
        return aIndex - bIndex;
      });
    },
    isPublished() {
      // ga, category, sectionCode, split, duplicae, delete
      return this.$app.props.status === EVENTSTATUS.PUBLISHED;
    },
    zoneColor() {
      return tinycolor(this.color.rgba).toRgbString();
    },
    unit() {
      return first(this.units)!;
    },
    units2() {
      return this.$app.seatmap.unitsFromSection(this.unit.uuid);
    },
    hasUnits() {
      return this.units2.length > 0;
    },
    isPolygon() {
      const pol = first(this.units)!;
      return pol.subtype === TYPE.POLYGON;
    },
    isRect() {
      return this.unit.subtype === TYPE.RECT;
    },
    isCircle() {
      return this.unit.subtype === TYPE.CIRCLE;
    },
    draftEvent() {
      return this.$app.props.status === EVENTSTATUS.DRAFT;
    },
    pauseEvent() {
      return this.$app.props.status === EVENTSTATUS.PAUSED;
    },
    eventIsPublished() {
      return this.$app.props.status === EVENTSTATUS.PUBLISHED;
    },
    hasDrawingMode() {
      return this.$app.props.options.modes.map((s) => s.toLowerCase()).includes('drawing');
    },
    categories() {
      if (this.isGa) {
        const gaCategories = this.$store.state.categories.categories.filter((category) => category.type === 'ga');

        // used categories
        let usedCategories: number[] = [];

        if (this.$app.props.oneCatPerGa) {
          usedCategories = this.$app.seatmap.sections.map((section) => section.categoryId).filter(Boolean);
        }

        const filteredCategories = gaCategories.filter(
          (category) => !usedCategories.includes(category.id) || category.id === this.unit.categoryId
        );

        if (filteredCategories.length > 0) {
          return filteredCategories.sort((a, b) => a.id - b.id);
        }

        return [
          {
            id: 0,
            name: 'No categories available',
            color: 'transparent',
            price: 0,
          },
        ];

        // return this.$store.state.categories.categories.filter((category) => category.type === 'ga');
      } else {
        return this.$store.state.categories.categories
          .filter((category) => category.type === 'seated')
          .sort((a, b) => a.id - b.id);
      }
    },
    visible: {
      get() {
        return this.unit.attributes.invisible !== true;
      },
      set(newValue: boolean) {
        newValue ? this.unit.makeVisible() : this.unit.makeInvisible();
        eventbus.emit('update:invisibleSeats');
        eventbus.emit('refreshUnassignedCount');
      },
    },
  },
  watch: {
    isGa(val) {
      this.unit.attributes.ga = val;
      if (val && this.unit.category && this.unit.category.type !== 'ga') {
        this.onCategorySelection(0);
      } else if (!val && this.unit.category && this.unit.category.type !== 'seated') {
        this.onCategorySelection(0);
      }
    },
  },
  created() {
    this.isGa = this.unit.attributes.ga;
    const { text, size, vOffset, hOffset, rotation, stroke, fill } = this.unit.attributes.label;
    this.label = text;
    this.fontSizeValue = size;
    this.fontColor = stroke !== undefined ? stroke : fill;
    this.vOffsetValue = vOffset;
    this.hOffsetValue = hOffset;
    this.rotationValue = rotation;
    // offsets for slider
    this.calculateOffsetMinMax();
    this.color = this.unit.attributes.color;
    this.smoothingValue = this.unit.attributes.smoothing * 500;
    if (this.isRect) {
      this.cornerRadius.value = (this.unit as unknown as RectangularSection).radius;
    }
    if (this.resizedUnit === this.unit.uuid) {
      eventbus.emit('section:units:lockPositionsAndShowInFront', this.unit.uuid);
    }
    this.serverSectionId = this.unit.attributes.serverSectionId;
    this.protectedFields = this.$app.props.sections?.protected ?? [];
    this.categoryId = this.unit.categoryId;
  },
  methods: {
    ...mapActions('seatmap', [
      'setResizing',
      'setResizedUnit',
      'setResizedUnitSmoothing',
      'enableSplitMode',
      'disableSplitMode',
    ]),
    ...mapActions('settings', [
      'setInsideZoneLayout',
      'enableSelection',
      'setDrawingMode',
      'setBlockSeatsMode',
      'disableSelection',
      'enableSelection',
    ]),
    onCategorySelection(categoryId: number) {
      if (this.unit.categoryId === categoryId) return;

      const category = this.$store.state.categories.categories.find((c) => c.id === categoryId)!;
      if (category && category.type === 'ga' && this.$app.seatmap.seats.some((s) => s.sid === this.unit.uuid)) {
        this.categoryId = categoryId;
        // @ts-expect-error
        this.$refs.confirmCategoryAssignmentRef!.open();
        return;
      }

      this.unit.assignCategory(categoryId);
      this.categoryId = categoryId;
      this.$app.seatmap.triggerUnsavedChanges(true);
    },
    onServerSectionIdSelection(serverSectionId: string | null, confirmed = false) {
      if (this.unit.attributes.serverSectionId === serverSectionId) return;

      const serverSection = this.$app.props.sections!.items.find((s) => s.id === serverSectionId);
      const cat = this.$store.state.categories.categories.find((c) => c.id === serverSection?.categoryId)!;

      if (cat && cat.type === 'ga' && !confirmed && this.$app.seatmap.seats.some((s) => s.sid === this.unit.uuid)) {
        this.serverSectionId = serverSectionId;
        // @ts-expect-error
        this.$refs.confirmServerSectionIdAssignmentRef!.open();
        return;
      }

      this.serverSectionId = serverSectionId;
      this.unit.syncWithServerSection(serverSectionId);
      this.isGa = this.unit.category?.type === 'ga';
      this.$app.seatmap.triggerUnsavedChanges(true);
    },
    serverSectionIdAssignmentDialogClosed(value: 'ok' | 'cancel') {
      if (value === 'ok') {
        this.$app.seatmap.deleteSectionUnits(this.unit.uuid);
        this.onServerSectionIdSelection(this.serverSectionId!, true);
      } else {
        this.serverSectionId = this.unit.attributes.serverSectionId;
      }
    },
    categoryAssignmentDialogClosed(value: 'ok' | 'cancel') {
      if (value === 'ok') {
        this.$app.seatmap.deleteSectionUnits(this.unit.uuid);
        this.onCategorySelection(this.categoryId!);
      } else {
        this.categoryId = this.unit.categoryId;
      }
    },
    calculateOffsetMinMax() {
      const { minVOffset, maxVOffset, minHOffset, maxHOffset } = this.unit;
      this.hOffset.min = minHOffset;
      this.hOffset.max = maxHOffset;
      this.vOffset.min = minVOffset;
      this.vOffset.max = maxVOffset;
    },
    onCornerRadiusChange(val: number) {
      (this.unit as unknown as RectangularSection).radius = val;
    },
    showInsides(sid: string) {
      const section = this.$app.seatmap.sections.find((u) => u.uuid === sid)!;
      section.showInsides();
    },
    hideInsides(sid: string) {
      const section = this.$app.seatmap.sections.find((u) => u.uuid === sid)!;
      section.hideInsides();
    },
    stopResizing() {
      const section = this.$app.seatmap.sections.find((u) => u.uuid === this.resizedUnit)!;
      section.endResizing();
    },
    resize() {
      if (this.resizedUnit === this.unit.uuid) {
        if (this.unit.subtype === TYPE.PATH) {
          (this.unit as unknown as PathSection).finishEdit();
        } else {
          this.stopResizing();
        }
      } else {
        if (this.unit.subtype === TYPE.PATH) {
          this.unit.edit();
        } else {
          this.unit.startResizing(
            () => this.unit.onResizing(),
            () => this.onResizingDone()
          );
        }
      }
    },
    onResizing() {
      this.calculateOffsetMinMax();
      this.vOffsetValue = 0;
      this.hOffsetValue = 0;
    },
    onResizingDone() {
      eventbus.emit('menu:reinit', true);
    },
    hideMenu() {
      this.$app.seatmap.hideContextMenu();
    },
    onLabelColorChanged(color: string) {
      this.unit.changeLabelStroke(color);
    },
    onHOffsetValueChange(value: number) {
      this.unit.changeHOffset(value);
      eventbus.emit('seatmap:updateSelectionBox');
    },
    onVOffsetValueChange(value: number) {
      this.unit.changeVOffset(value);
      eventbus.emit('seatmap:updateSelectionBox');
    },
    onFontSizeValueChange(value: number) {
      this.unit.changeFontSize(value);
      lastUsedSectionFontSize.value = value;
      eventbus.emit('seatmap:updateSelectionBox');
    },
    onOpacityChanged(value: number) {
      this.unit.changeOpacity(value);
    },
    onSmoothingChange(value: number) {
      this.unit.attributes.smoothing = value / 500;
      // @ts-expect-error
      this.unit.plot();
    },
    onRotationValueChange(value: number) {
      this.unit.changeRotation(value);
    },
    onInputClick() {
      // intentionally left blank
    },
    onLabelChange(value: string) {
      this.unit.changeLabel(value);
    },
    flipHorizontally() {
      eventbus.emit('zone:flip', this.unit, FLIP.HORIZONTALLY);
    },
    flipVertically() {
      eventbus.emit('zone:flip', this.unit, FLIP.VERTICALLY);
    },
    duplicate() {
      eventbus.emit('selection:clone');
    },
    remove() {
      eventbus.emit('selection:remove');
    },
    edit() {
      this.$app.currentSection = this.unit;
      this.enableSelection();
      if (this.$app.props.status === EVENTSTATUS.PUBLISHED) {
        this.setBlockSeatsMode();
      } else {
        this.setDrawingMode();
      }
      this.unit.addClass('section-shape');
      this.setInsideZoneLayout();
      eventbus.emit('seatmap:labels:updated');
    },
    onChangeEntrance() {
      this.$app.seatmap.triggerUnsavedChanges(true);

      this.unit.attributes.entrance = this.entrance;
    },
    onSplitStart() {
      if (this.splitModeUnit) {
        this.hideInsides(this.splitModeUnit);
      }
      this.enableSplitMode(this.unit.uuid);
      this.showInsides(this.unit.uuid);
      // this.$app.seatmap.clearSelection();
      this.$app.seatmap.selection.purge();
      eventbus.emit('selection:updated', this.$app.seatmap.selection);
      this.disableSelection();
    },
    onSplitEnd() {
      this.hideInsides(this.unit.uuid);
      this.disableSplitMode();
      this.enableSelection();
      this.$app.seatmap.selection.add(this.unit);
      eventbus.emit('selection:updated', this.$app.seatmap.selection);
    },
    split() {
      if (this.splitModeUnit === this.unit.uuid) {
        this.onSplitEnd();
      } else {
        this.onSplitStart();
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.md-small {
  width: auto;
  padding: 2px 3px;
  min-height: 20px;
  font-size: 0.9em;
  line-height: 2em;
}
.md-input-container {
  min-height: auto !important;
  margin: 0 !important;
  padding-top: 0;

  label {
    top: 10px;
  }
}
.md-input-focused {
  label {
    display: none;
  }
}
div {
  -webkit-user-select: none;
  user-select: none;
}
body {
  -moz-user-focus: ignore;
  -moz-user-select: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -o-user-select: none;
  user-select: none;
}
</style>
